import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { format, parseISO, subMonths } from 'date-fns';
import { Users, UserPlus, ClipboardList, Clock, TrendingUp, Activity, Calendar, MapPin, Download } from 'lucide-react';

import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import HelpTooltip from '../../components/UI/HelpTooltip';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const buildMonthlySeries = (source = [], months = 6) => {
  const totals = new Map();
  source?.forEach((item) => {
    if (item?.month) {
      totals.set(item.month, item.total ?? 0);
    }
  });

  const series = [];
  const now = new Date();

  for (let i = months - 1; i >= 0; i -= 1) {
    const date = subMonths(now, i);
    const key = format(date, 'yyyy-MM');
    series.push({
      monthKey: key,
      label: format(date, 'MMM yyyy'),
      total: totals.get(key) ?? 0,
    });
  }

  return series;
};

const DashboardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
`;

const SummaryCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: ${props => props.theme.shadows.md};
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color + '1A'};
  color: ${props => props.color};
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 1.5rem;
`;

const LargeCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(520px, 1fr));
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ChartCard = styled(Card)`
  padding: 1.5rem;
  box-shadow: ${props => props.theme.shadows.md};
`;

const ChartTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const TableCard = styled(Card)`
  padding: 1.5rem;
  box-shadow: ${props => props.theme.shadows.md};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const TableHead = styled.thead`
  background: ${props => props.theme.colors.background.secondary};
`;

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 0.75rem 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const TableCell = styled.td`
  padding: 0.65rem 0.5rem;
  color: ${props => props.theme.colors.text.primary};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background: ${props => props.bg};
  color: ${props => props.color};
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const LoadingContainer = styled(Card)`
  padding: 2rem;
  text-align: center;
`;

const RoleBasedDashboard = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    let isMounted = true;
    const fetchSummary = async () => {
      try {
        const { data } = await dashboardAPI.getSummary();
        if (isMounted) {
          setSummary(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
        setLoading(false);
        }
      }
    };
    
    fetchSummary();
    return () => {
      isMounted = false;
    };
  }, []);

  const statusColors = useMemo(() => ({
    submitted: theme.colors.info,
    under_review: theme.colors.warning,
    committee_review: theme.colors.warning,
    eligible: theme.colors.success,
    resubmit: theme.colors.error,
    approved: theme.colors.success,
    rejected: theme.colors.error,
    active: theme.colors.success,
    inactive: theme.colors.text.secondary,
    pending: theme.colors.warning,
    central_pending: theme.colors.warning,
    regional_pending: theme.colors.warning,
    draft: theme.colors.info,
  }), [theme.colors]);

  const applicationStatusChart = useMemo(() => {
    if (!summary?.applications?.status_counts) return null;
    const entries = Object.entries(summary.applications.status_counts);
    if (!entries.length) return null;

    const labels = entries.map(([status]) => status.replace(/_/g, ' '));
    const data = entries.map(([status, value]) => value);
    const colors = entries.map(([status]) => statusColors[status] || theme.colors.primary);

    return {
      labels,
      datasets: [
        {
          label: 'Applications',
          data,
          backgroundColor: colors,
          borderWidth: 1,
        },
      ],
    };
  }, [summary, statusColors, theme.colors.primary]);

  const membersByRegionChart = useMemo(() => {
    if (!summary?.members?.by_region?.length) return null;
    const source = summary.members.by_region.slice(0, 10);
    return {
      labels: source.map(item => item.region),
      datasets: [
        {
          label: 'Members',
          data: source.map(item => item.total),
          backgroundColor: theme.colors.primary,
          borderRadius: 6,
        },
      ],
    };
  }, [summary, theme.colors.primary]);

  const membershipTypeChart = useMemo(() => {
    if (!summary?.members?.by_type?.length) return null;
    const palette = [theme.colors.primary, theme.colors.success, theme.colors.warning, theme.colors.info];
    return {
      labels: summary.members.by_type.map(item => (item.membership_type || 'Unknown').replace(/_/g, ' ')),
      datasets: [
        {
          label: 'Members',
          data: summary.members.by_type.map(item => item.total),
          backgroundColor: summary.members.by_type.map((_item, index) => palette[index % palette.length]),
          borderRadius: 6,
        },
      ],
    };
  }, [summary, theme.colors]);

  const genderBreakdownChart = useMemo(() => {
    if (!summary?.members?.by_gender?.length) return null;
    const palette = [theme.colors.primary, theme.colors.success, theme.colors.warning, theme.colors.info];
    return {
      labels: summary.members.by_gender.map(item => (item.gender || 'Unspecified').replace(/_/g, ' ')),
      datasets: [
        {
          data: summary.members.by_gender.map(item => item.total),
          backgroundColor: summary.members.by_gender.map((_item, index) => palette[index % palette.length]),
          borderWidth: 0,
        },
      ],
    };
  }, [summary, theme.colors]);

  const newMembersTrend = useMemo(() => {
    const series = buildMonthlySeries(summary?.members?.monthly_new, 6);
    if (!series.length) return null;
    return {
      labels: series.map(item => item.label),
      datasets: [
        {
          label: 'New Members',
          data: series.map(item => item.total),
          backgroundColor: theme.colors.primary,
          borderRadius: 6,
        },
      ],
    };
  }, [summary, theme.colors.primary]);

  const applicationsTrend = useMemo(() => {
    const series = buildMonthlySeries(summary?.applications?.monthly_trend, 6);
    if (!series.length) return null;
    return {
      labels: series.map(item => item.label),
      datasets: [
        {
          label: 'Applications',
          data: series.map(item => item.total),
          backgroundColor: theme.colors.warning,
          borderRadius: 6,
        },
      ],
    };
  }, [summary, theme.colors.warning]);

  const comparativeMonthlyData = useMemo(() => {
    const appSeries = buildMonthlySeries(summary?.applications?.monthly_trend, 6);
    const memberSeries = buildMonthlySeries(summary?.members?.monthly_new, 6);

    if (!appSeries.length && !memberSeries.length) {
      return null;
    }

    const memberMap = new Map(memberSeries.map(item => [item.monthKey, item.total]));
    const combined = appSeries.map(item => ({
      monthKey: item.monthKey,
      label: item.label,
      applications: item.total,
      members: memberMap.get(item.monthKey) ?? 0,
    }));

    return {
      labels: combined.map(item => item.label),
      rows: combined.map(item => ({
        label: item.label,
        applications: item.applications,
        members: item.members,
      })),
      datasets: [
        {
          label: 'Applications Submitted',
          data: combined.map(item => item.applications),
          backgroundColor: theme.colors.primary,
          borderRadius: 6,
        },
        {
          label: 'New Members',
          data: combined.map(item => item.members),
          backgroundColor: theme.colors.success,
          borderRadius: 6,
        },
      ],
    };
  }, [summary, theme.colors.primary, theme.colors.success]);
  
  if (loading) {
    return (
      <LoadingContainer>
        <div>Loading dashboard data…</div>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <div>Unable to load dashboard data. Please try again later.</div>
      </LoadingContainer>
    );
  }

  const cards = summary?.summary_cards || {};
  const summaryItems = [
    {
      label: 'Total Members',
      value: cards.total_members ?? 0,
      icon: <Users size={24} />,
      color: theme.colors.primary,
    },
    {
      label: 'Active Members',
      value: cards.active_members ?? 0,
      icon: <TrendingUp size={24} />,
      color: theme.colors.success,
    },
    {
      label: 'Pending Applications',
      value: cards.pending_applications ?? 0,
      icon: <ClipboardList size={24} />,
      color: theme.colors.warning,
    },
    {
      label: 'Pending Registrations',
      value: cards.pending_registrations ?? 0,
      icon: <UserPlus size={24} />,
      color: theme.colors.info,
    },
    {
      label: 'Applications Today',
      value: cards.applications_today ?? 0,
      icon: <Activity size={24} />,
      color: theme.colors.primary,
    },
    {
      label: 'Avg Review Time (hrs)',
      value: cards.average_review_time_hours ?? '—',
      icon: <Clock size={24} />,
      color: theme.colors.secondary,
    },
  ];

  const recentApplications = summary?.applications?.recent || [];
  const recentMembers = summary?.members?.recent || [];
  const upcomingEvents = summary?.events?.upcoming || [];
  const topTrainings = summary?.trainings?.top || [];
  
  return (
    <DashboardWrapper>
      <Header>
        <div>
          <Title theme={theme}>
            Welcome back{user?.first_name ? `, ${user.first_name}` : ''}!
            <HelpTooltip>
              <p>This dashboard gives you a quick overview of how membership and applications are performing.</p>
              <ul>
                <li>Use the cards to see totals for members, active members, and pending applications.</li>
                <li>Charts show trends over time and help you see where applications or approvals are increasing or slowing.</li>
                <li>Scroll down to review latest applications, new members, and upcoming events.</li>
              </ul>
            </HelpTooltip>
          </Title>
          <Subtitle theme={theme}>Here is the latest snapshot of membership, applications, and engagements.</Subtitle>
        </div>
        <Button variant="primary" leftIcon={<Download size={16} />} onClick={() => window.location.href = '/app/reports'}>
          View Reports
          </Button>
      </Header>
      
      <SummaryGrid>
        {summaryItems.map((item) => (
          <SummaryCard key={item.label}>
            <IconWrap color={item.color}>{item.icon}</IconWrap>
            <div>
              <StatValue theme={theme}>{item.value}</StatValue>
              <StatLabel theme={theme}>{item.label}</StatLabel>
            </div>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <LargeCardGrid>
        <ChartCard>
          <ChartTitle theme={theme}>Application Status Distribution</ChartTitle>
          {applicationStatusChart ? (
            <Pie data={applicationStatusChart} options={{ plugins: { legend: { position: 'bottom' } } }} />
          ) : (
            <EmptyState theme={theme}>No application data available yet.</EmptyState>
          )}
        </ChartCard>
        <ChartCard>
          <ChartTitle theme={theme}>Members by Region (Top 10)</ChartTitle>
          {membersByRegionChart ? (
            <Bar
              data={membersByRegionChart}
              options={{
                plugins: { legend: { display: false } },
                indexAxis: 'y',
                scales: {
                  x: { beginAtZero: true, title: { display: true, text: 'Members' } },
                  y: { title: { display: true, text: 'Region' } },
                },
              }}
            />
          ) : (
            <EmptyState theme={theme}>No regional membership data available.</EmptyState>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle theme={theme}>Membership Types</ChartTitle>
          {membershipTypeChart ? (
            <Bar
              data={membershipTypeChart}
              options={{
                plugins: { legend: { display: false } },
                indexAxis: 'y',
                scales: {
                  x: { beginAtZero: true, title: { display: true, text: 'Members' } },
                  y: { title: { display: true, text: 'Type' } },
                },
              }}
            />
          ) : (
            <EmptyState theme={theme}>No membership type data recorded.</EmptyState>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle theme={theme}>Members by Gender</ChartTitle>
          {genderBreakdownChart ? (
            <Pie
              data={genderBreakdownChart}
              options={{
                plugins: { legend: { position: 'bottom' } },
              }}
            />
          ) : (
            <EmptyState theme={theme}>No gender data captured yet.</EmptyState>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle theme={theme}>New Members (Last 6 Months)</ChartTitle>
          {newMembersTrend ? (
            <Bar
              data={newMembersTrend}
              options={{
                plugins: { legend: { display: false } },
                scales: {
                  x: { title: { display: true, text: 'Months' } },
                  y: { beginAtZero: true, title: { display: true, text: 'Users' } },
                },
              }}
            />
          ) : (
            <EmptyState theme={theme}>No recent member registration data available.</EmptyState>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle theme={theme}>Applications Submitted (Last 6 Months)</ChartTitle>
          {applicationsTrend ? (
            <Bar
              data={applicationsTrend}
              options={{
                plugins: { legend: { display: false } },
                scales: {
                  x: { title: { display: true, text: 'Months' } },
                  y: { beginAtZero: true, title: { display: true, text: 'Applications' } },
                },
              }}
            />
          ) : (
            <EmptyState theme={theme}>No application submissions recorded yet.</EmptyState>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle theme={theme}>Applications vs. New Members (Last 6 Months)</ChartTitle>
          {comparativeMonthlyData ? (
            <>
              <Bar
                data={{
                  labels: comparativeMonthlyData.labels,
                  datasets: comparativeMonthlyData.datasets,
                }}
                options={{
                  plugins: {
                    legend: { position: 'bottom' },
                    title: {
                      display: true,
                      text: 'Applications vs. New Members (Last 6 Months)',
                    },
                  },
                  responsive: true,
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: 'Months',
                      },
                    },
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Count',
                      },
                    },
                  },
                }}
              />
              <StyledTable style={{ marginTop: '1rem' }}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell theme={theme}>Month</TableHeaderCell>
                    <TableHeaderCell theme={theme}>Applications Submitted</TableHeaderCell>
                    <TableHeaderCell theme={theme}>New Members</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {comparativeMonthlyData.rows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell theme={theme}>{row.label}</TableCell>
                      <TableCell theme={theme}>{row.applications}</TableCell>
                      <TableCell theme={theme}>{row.members}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </StyledTable>
            </>
          ) : (
            <EmptyState theme={theme}>Submit data to compare application and member growth.</EmptyState>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle theme={theme}>Top Trainings by Participation</ChartTitle>
          {topTrainings.length ? (
            <Bar
              data={{
                labels: topTrainings.map(item => item.title || 'Unknown'),
                datasets: [
                  {
                    label: 'Participants',
                    data: topTrainings.map(item => item.total),
                    backgroundColor: theme.colors.success,
                    borderRadius: 6,
                  },
                ],
              }}
              options={{
                plugins: { legend: { display: false } },
                scales: {
                  x: { beginAtZero: true },
                },
              }}
            />
          ) : (
            <EmptyState theme={theme}>Participation data will appear here once members are assigned to trainings.</EmptyState>
          )}
        </ChartCard>
      </LargeCardGrid>

      <SectionGrid>
        <TableCard>
          <ChartTitle theme={theme}>Latest Applications</ChartTitle>
          {recentApplications.length ? (
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableHeaderCell theme={theme}>Applicant</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Status</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Type</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Submitted</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {recentApplications.map((item) => {
                  const status = item.status || 'submitted';
                  const badgeColor = statusColors[status] || theme.colors.primary;
                  return (
                    <TableRow key={item.id}>
                      <TableCell theme={theme}>{item.applicant_name || '—'}</TableCell>
                      <TableCell theme={theme}>
                        <StatusBadge bg={badgeColor + '20'} color={badgeColor}>
                          {status.replace(/_/g, ' ')}
                        </StatusBadge>
                      </TableCell>
                      <TableCell theme={theme}>{(item.type || 'public').replace(/_/g, ' ')}</TableCell>
                      <TableCell theme={theme}>{item.created_at ? format(parseISO(item.created_at), 'dd MMM yyyy') : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </StyledTable>
          ) : (
            <EmptyState theme={theme}>No applications have been submitted yet.</EmptyState>
          )}
        </TableCard>

        <TableCard>
          <ChartTitle theme={theme}>Recent Members</ChartTitle>
          {recentMembers.length ? (
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableHeaderCell theme={theme}>Name</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Status</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Type</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Joined</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {recentMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell theme={theme}>{`${member.first_name || ''} ${member.last_name || ''}`.trim() || '—'}</TableCell>
                    <TableCell theme={theme}>
                      <StatusBadge
                        bg={(statusColors[member.membership_status] || theme.colors.primary) + '20'}
                        color={statusColors[member.membership_status] || theme.colors.primary}
                      >
                        {(member.membership_status || 'pending').replace(/_/g, ' ')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell theme={theme}>{(member.membership_type || '—').replace(/_/g, ' ')}</TableCell>
                    <TableCell theme={theme}>{member.created_at ? format(parseISO(member.created_at), 'dd MMM yyyy') : '—'}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </StyledTable>
          ) : (
            <EmptyState theme={theme}>New member registrations will appear here.</EmptyState>
          )}
        </TableCard>

        <TableCard>
          <ChartTitle theme={theme}>Upcoming Events</ChartTitle>
          {upcomingEvents.length ? (
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableHeaderCell theme={theme}>Event</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Date</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Type</TableHeaderCell>
                  <TableHeaderCell theme={theme}>Location</TableHeaderCell>
                </TableRow>
              </TableHead>
              <tbody>
                {upcomingEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell theme={theme}>{event.title}</TableCell>
                    <TableCell theme={theme}>{event.start_date ? format(parseISO(event.start_date), 'dd MMM yyyy') : '—'}</TableCell>
                    <TableCell theme={theme}>{(event.event_type || '—').replace(/_/g, ' ')}</TableCell>
                    <TableCell theme={theme}>{event.location || '—'}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </StyledTable>
          ) : (
            <EmptyState theme={theme}>No upcoming events scheduled.</EmptyState>
          )}
        </TableCard>
      </SectionGrid>
    </DashboardWrapper>
  );
};

export default RoleBasedDashboard;
